import { Directive, OnInit } from '@angular/core';
import { AbstractControlOptions, FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, NavigationExtras } from '@angular/router';
import { Subject } from 'rxjs';

import { hasDifferentPropertyValue } from '~/core/helpers/object/has-different-property-value/has-different-property-value.helper';
import { FormGroupValueInterface } from '~/component/form/interfaces/form-group-value.interface';
import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { TitleService } from '~/core/services/title/title.service';

@Directive()
export abstract class AbstractOrganizerSharedFormPage<T, U> implements OnInit {
  public readonly error$ = new Subject<boolean>();
  public readonly loading$ = new Subject<boolean>();

  protected abstract readonly pageTitleKey: string;
  protected enableRedirectUrl = false; // Authorize the external redirection with toggle service.
  protected formBuilderOptions: AbstractControlOptions = {};
  protected useRedirectUrl = false; // The page can use redirectUrl query for external redirection.
  protected validationConfig: { [key: string]: unknown } = {};
  public formGroup: FormGroup;

  protected constructor(
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly formBuilder: FormBuilder,
    protected readonly navigationService: NavigationService,
    protected readonly routeNames: RoutingInterface[],
    protected readonly titleService: TitleService
  ) {}

  public ngOnInit(): void {
    this.beforeInitHook();

    this.titleService.setPageTitle(this.pageTitleKey);

    this.buildValidationConfig();
    this.buildFormBuilderOptions();
    this.buildForm();
    this.onInitHook();
  }

  protected abstract buildValidationConfig(): void;
  public abstract submit(formGroupValue: FormGroupValueInterface): void;

  private buildForm(): void {
    this.formGroup = this.formBuilder.group(
      this.validationConfig,
      this.formBuilderOptions
    );
  }

  private redirectToExternalUrl(): boolean {
    if (!this.enableRedirectUrl) {
      return false;
    }

    /**
     * Guard already checks if this redirectUrl is valid.
     * @see src/app/organizer/shared/guards/is-authorized-redirect-url/is-authorized-redirect-url.guard.ts
     */
    window.location.href = this.activatedRoute.snapshot.queryParams.redirectUrl;

    return true;
  }

  protected beforeInitHook(): void {
    // Can be override in pages if needed
  }

  /**
   * T: Entity body interface.
   * U: Entity interface only if no locatable attribute.
   */
  protected buildDataBeforeSubmit(
    _formGroupValue?: FormGroupValueInterface
  ): Partial<T | U> {
    return {};
  }

  protected buildFormBuilderOptions(): void {
    // Can be override in pages if needed
  }

  protected onInitHook(): void {
    // Can be override in pages if needed
  }

  protected redirectAfterSubmit(
    navigationExtras: NavigationExtras = {}
  ): Promise<boolean> {
    if (this.redirectToExternalUrl()) {
      return Promise.resolve(true);
    }

    return this.navigationService.nextNavigate(
      this.routeNames,
      this.activatedRoute,
      navigationExtras
    );
  }

  protected redirectIfNoDataChanges(
    data: Partial<T>,
    originalData: object
  ): false | Promise<boolean> {
    if (!hasDifferentPropertyValue(data, originalData)) {
      if (this.redirectToExternalUrl()) {
        return Promise.resolve(true);
      }

      return this.redirectAfterSubmit();
    }

    return false;
  }
}
